<template>
  <v-container>
    <v-flex xs12>
      <v-card class="pa-4 mb-4" v-if="generatedJSON">
        <v-layout align-center>
          <v-flex xs12>
            <div class="json-field pa-3">{{generatedJSON}}</div>
            <input type="text" id="json-field" :value="generatedJSON">
          </v-flex>
          <v-flex xs12 text-xs-right>
            <v-layout row wrap justify-space-between align-center>
              <v-btn color="primary" class="mx-0" @click="copyJSON">Copy</v-btn>
              <v-btn color="error" class="mx-0" @click="generatedJSON = ''">Close</v-btn>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-card>
      <v-card class="py-4">
        <v-flex xs12 pb-3 px-4 v-if="err">
          <v-alert type="error" :value="true">{{err}}</v-alert>
        </v-flex>
        <v-flex xs12 pb-3  px-4 v-if="success">
          <v-alert type="success" :value="true">{{success}}</v-alert>
        </v-flex>
        <v-form v-model="valid">
          <v-flex xs12 sm8 md6 lg4 px-4>
            <v-autocomplete
              required
              :loading="companyLoading"
              :rules="[v => !!v || `${$lang.errors.missingCompany}`]"
              :disabled="isEdit || companies.length === 0"
              :items="companies"
              item-text="name"
              item-value="id"
              v-model="selectedCompany"
              :label="$lang.actions.selectCompany"
            />
          </v-flex>
          <v-flex xs12 sm8 md6 lg4 px-4>
            <v-autocomplete
              required
              :rules="[() => selectedDomains.length > 0 || `${$lang.errors.missingDomain}`]"
              :loading="domainLoading"
              multiple
              chips
              :disabled="mappedDomains.length === 0"
              deletable-chips
              :items="mappedDomains"
              v-model="selectedDomains"
              :label="$lang.actions.selectDomain"
            />
          </v-flex>
          <v-flex xs12 sm8 md6 lg4 px-4>
            <v-autocomplete
              required
              :rules="[v => !!v || `${$lang.errors.missingTemplateType}`]"       
              :loading="typesLoading"
              :disabled="templateTypes.length === 0"
              :items="sortedTemplateTypes"
              v-model="selectedType"
              :label="$lang.actions.selectTemplateType"
            />
          </v-flex>
          <v-flex xs12 sm8 md6 lg4 px-4>
            <v-autocomplete
              required
              :rules="[v => !!v || `${$lang.errors.missingTemplate}`]"       
              :disabled="filteredTempaltes.length === 0"
              :loading="templatesLoading"
              :items="filteredTempaltes"
              v-model="selectedTemplate"
              :label="$lang.actions.selectTemplate"
            />
          </v-flex>
          <v-flex xs12 sm8 md6 lg4 px-4>
            <v-text-field
              label="Description"
              placeholder="Description of the domain"
              v-model="domainDescription"
            />
          </v-flex>
          <v-btn class="primary mx-4" @click="addProperty()" :disabled="editingProp">{{$lang.actions.addProperty}}</v-btn>
          <div class="v-table__overflow my-3 px-4">
            <table class="v-datatable v-table theme--light">
              <tbody>
                <tr 
                  v-for="(property, index) in properties"
                  :key="index">
                  <template v-if="property.isEdit"> 
                    <td class="min-w-lg pb-2">
                      <v-select
                        required
                        :items="propertyTypes"
                        v-model="tempProp.type"
                        :label="$lang.actions.selectPropType"
                        hide-details
                      />
                    </td>
                    <td  class="pb-2 min-w-lg">
                      <v-select
                        v-if="tempProp.type === 'TEXT'"
                        :items="sortedTextTypes"
                        :label="$lang.labels.propKey"
                        v-model="tempProp.name"
                        hide-details
                      />
                      <v-select
                        v-if="tempProp.type === 'URL'"
                        :label="$lang.actions.selectUrl"
                        :items="sortedUrlTypes"
                        v-model="tempProp.name"
                        hide-details
                      />
                    </td>
                    <td class="pb-2">
                      <v-text-field
                        :label="$lang.labels.propValue"
                        v-model="tempProp.value"
                        hide-details
                      /></td>
                    <td class="min-w-lg px-0">
                      <v-btn class="primary elevation-1" :disabled="!tempProp.type || !tempProp.name || !tempProp.value" @click="savePropEdit(property)">{{$lang.actions.save}}</v-btn>
                      <v-btn class="error ml-0 elevation-1" @click="closePropEdit(property, index)">{{$lang.actions.cancel}}</v-btn>
                    </td>
                  </template>
                  <template v-else>
                    <td>{{ property.type }}</td>
                    <td>{{ property.name}}</td>
                    <td>{{ property.value}}</td>
                    <td class="actions px-0">
                      <v-btn class="primary elevation-1" @click="editProp(property)">{{$lang.actions.edit}}</v-btn>
                      <v-btn class="error ml-0 elevation-1" @click="properties.splice(index, 1)">{{$lang.actions.delete}}</v-btn>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
          <v-layout row wrap justify-space-between class="px-2">
            <v-text-field
              v-if="isEdit && createdOn"
              :label="$lang.labels.created"
              v-model="createdOn"
              readonly
              class="min-w max-w mx-3"
            />
            <v-text-field
              v-if="isEdit && modifiedOn"
              :label="$lang.labels.modified"
              v-model="modifiedOn"
              readonly
              class="min-w max-w mx-3"
            />
          </v-layout>
          <v-layout justify-space-between class="px-4">
            <v-btn flat class="ml-0" @click="$router.push({ name: 'webSpaces' })"><v-icon>keyboard_arrow_left</v-icon>{{$lang.actions.close}}</v-btn>
            <div>
              <v-btn class="primary" :disabled="!valid" @click="generateJSON" v-if="isEdit">{{$lang.actions.generateJson}}</v-btn>
              <v-btn class="primary mx-0" :disabled="!valid" @click="submit">{{$lang.actions.submit}}</v-btn>
            </div>
          </v-layout>
        </v-form>
      </v-card>
    </v-flex>
  </v-container>
</template>

<script>
  import {DateTime} from 'luxon';

  export default {
    data: () => ({
      err: '',
      success: '',
      isEdit: false,
      valid: false,
      companyLoading: true,
      typesLoading: true,
      templatesLoading: true,
      domainLoading: false,
      companies: [],
      selectedCompany: '',
      domains: [],
      selectedDomains: [],
      templateTypes: [],
      selectedType: null,
      templates: null,
      selectedTemplate: null,
      domainDescription: '',
      properties: [],
      propertiesAll: [],
      propertyTypes: ['TEXT', 'URL'],
      urlTypes: [],
      textTypes: [],
      editingProp: false,
      tempProp: {
        type: '',
        name: '',
        value: '',
      },
      generatedJSON: '',
      createdOn: '',
      modifiedOn: '',
    }),
    watch: {
      selectedCompany(val) {
        this.fetchDomainforCompany(val);
      },
      err(val) {
        if (val !== '') {
          setTimeout(() => { this.err = ''; }, 5000);
        }
      },
    },
    computed: {
      filteredTempaltes() {
        return this.selectedType === null || this.templates === null ?
          [] :
          Object.keys(this.templates).filter((key) => this.templates[key] === this.selectedType).sort();
      },
      mappedDomains() {
        return this.domains.map(domain => ({
          text: `${domain.domain} (${domain.status})`,
          value: domain.id,
        }));
      },
      sortedTemplateTypes() {
        return this.templateTypes = this.templateTypes.sort();
      },
      sortedUrlTypes() {
        for (let i = 0; i < this.propertiesAll.length; i++) {
          if (this.propertiesAll[i].type === 'URL')
          this.urlTypes.push(this.propertiesAll[i].name);
        }
        return this.urlTypes = this.urlTypes.sort();
      },
      sortedTextTypes() {
        for (let i = 0; i < this.propertiesAll.length; i++) {
          if (this.propertiesAll[i].type === 'TEXT')
          this.textTypes.push(this.propertiesAll[i].name);
        }
        return this.textTypes = this.textTypes.sort();
      },
    },
    methods: {
      formatDate(date) {
         return DateTime.fromISO(date).toFormat('yyyy-MM-dd HH:mm:ss');
      },
      init() {
        return new Promise((resolve) => {
          Promise.all([
            this.$http.get('company')
            .then((res) => {
              this.companyLoading = false;
              if (res.body.status === 'Success') {
                this.companies = res.body.companies;
                return;
              }
              this.err = res.body.statusText;
            }, (err) => {
              this.companyLoading = false;
              this.err = err.body.statusText || err.body.message;
            }),
            this.$http.get('system/webspace/fetchtypes')
              .then((res) => {
                this.typesLoading = false;
                if (res.body.status === 'Success') {
                  this.templateTypes = res.body.types;
                  return;
                }
                this.err = res.body.statusText;
              }, (err) => {
                this.typesLoading = false;
                this.err = err.body.statusText || err.body.message;
              }),
            this.$http.get('system/webspace/fetchtemplates')
              .then((res) => {
                if (res.body.status === 'Success') {
                  this.templatesLoading = false;
                  this.templates = res.body.templates;
                  return;
                }
                this.err = res.body.statusText;
              }, (err) => {
                this.templatesLoading = false;
                this.err = err.body.statusText || err.body.message;
              }),
              this.$http.get('system/webspace/fetchproperties')
                .then((res) => {
                  if (res.body.status === 'Success') {
                    this.propertiesAll = Object.keys(res.body.properties).map(key => ({
                      type: res.body.properties[key],
                      name: key,
                      value: '',
                      isEdit: false,
                    }));
                    return;
                  }
                  this.err = res.body.statusText;
                }, (err) => {
                  this.err = err.body.statusText || err.body.message;
                })
          ]).then(() => resolve());
        });
      },
      fetchDomainforCompany(id) {
        this.domainLoading = true;
        this.domains = [];
        this.$http.get(`domain/fetchallforcompany/${id}`)
          .then((res) => {
            this.domainLoading = false;
            if (res.body.status === 'Success') {
              this.domains = res.body.domains;
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            this.domainLoading = false;
            this.err = err.body.statusText || err.body.message;
          });
      },
      addProperty() {
        this.properties.push({
          type: '',
          name: '',
          value: '',
          isEdit: true,
        });
        this.editingProp = true; 
      },
      editProp(property) {
        this.tempProp = {
          type: property.type,
          name: property.name,
          value: property.value,
        };
        this.$set(property, 'isEdit', true);
        this.editingProp = true; 
      },
      savePropEdit(property) {
        this.$set(property, 'type', this.tempProp.type);
        this.$set(property, 'name', this.tempProp.name);
        this.$set(property, 'value', this.tempProp.value);
        this.closePropEdit(property)
      },
      closePropEdit(property, index) {
        this.tempProp = {
          type: '',
          name: '',
          value: '',
        };
        if (property.type === '' && property.name === '' && property.value === '') {
          this.properties.splice(index, 1);
        } else {
          this.$set(property, 'isEdit', false);
        }
        this.editingProp = false;
      },
      generateJSON() {
        const data = [];
        this.$http.get(`company/${this.selectedCompany}`)
          .then((res) => {
            this.typesLoading = false;
            if (res.body.status === 'Success') {
              const company = res.body.company;
              data.push(
                {
                  name: 'companyId',
                  value: this.selectedCompany || null,
                },
                {
                  name: 'description',
                  value: this.domainDescription || null,
                },
                {
                  name: 'type',
                  value: this.selectedType || null,
                },
                {
                  name: 'domains',
                  value: this.selectedDomains || null,
                },
                {
                  name: 'template',
                  value: this.selectedTemplate || null,
                },
                {
                  name: 'properties',
                  value: this.props || null,
                },
                {
                  name: 'companyName',
                  value: company.name || null,
                },
                {
                  name: 'companyAddressLine1',
                  value: company.address.line1 || null,
                },
                {
                  name: 'companyAddressLine2',
                  value: company.address.line2 || null,
                },
                {
                  name: 'companyAddressLine3',
                  value: company.address.line3 || null,
                },
                {
                  name: 'companyAddressLine4',
                  value: company.address.line4 || null,
                },
                {
                  name: 'companyLocality',
                  value: company.address.locality || null,
                },
                {
                  name: 'companyPostalCode',
                  value: company.address.postalCode || null,
                },
                {
                  name: 'companyTownCity',
                  value: company.address.townCity || null,
                },
                {
                  name: 'companyPhone',
                  value: company.phone || null,
                },
                {
                  name: 'companyPhone',
                  value: company.phone || null,
                },
                {
                  name: 'companyCuisines',
                  value: company.cuisines || null,
                },
                {
                  name: 'companyOpeningDaysAndHours',
                  value: company.openingDaysAndHours || null,
                },
                {
                  name: 'companyS3ID',
                  value: company.companyRandomId || null,
                },
                {
                  name: 'companyEnableContactForm',
                  value: company.enableContactForm || false
                },
                {
                  name: 'companyEnableReservationForm',
                  value: company.enableReservationForm || false
                },
              );
              return this.$http.get('webshopresource/getall');
            }
            this.err = res.body.statusText;
          }, (err) => {
            this.err = err.body.statusText || err.body.message;
          })
          .then((res) => {
            if (res.body.status === 'Success') {
              const webshopInstance = res.body.details.find(webshop => webshop.companyId === this.selectedCompany);
              data.push({
                name: 'companyWebshopResourceId',
                value: webshopInstance ? webshopInstance.webShopResourceExternalId : null,
              });
              this.generatedJSON = JSON.stringify(data);
            }
            this.err = res.body.statusText;            
          }, (err) => {
            this.err = err.body.statusText || err.body.message;            
          })          
      },
      copyJSON() {
        try {
          var copyText = document.getElementById('json-field');
          copyText.select();
          const success = document.execCommand('copy');
        } catch (e) {
          console.log(e);
        }

      },
      submit() {
        const props = this.properties.filter((prop) => {
          return prop.value.length !== 0;
        });
        const data = {
          companyId: this.selectedCompany,
          description: this.domainDescription,
          type: this.selectedType,
          domains: this.selectedDomains,
          template: this.selectedTemplate,
          properties: props,
        };
        this.$http.post(this.isEdit ? `webspace/update/${this.$route.params.id}` : 'webspace/create', data)
          .then((res) => {
            if (res.body.status === 'Success') {
              this.success = this.isEdit ? 'Webspace updated successfuly' : 'Webspace created successfuly';
              setTimeout(() => {
                if (this.isEdit) {
                  this.success = '';
                  this.properties = props;
                } else {
                  this.$router.push({ name: 'webSpaces' });
                }
              }, 3000);
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            this.err = err.body.statusText || err.body.message;
          });
      },
    },
    created() {
      this.init()
        .then(() => {
          if (this.$route.params.id) {
            this.isEdit = true;
            this.$http.get(`webspace/${this.$route.params.id}`)
              .then((res) => {
                if (res.body.status === 'Success') {
                  const webspace = res.body.webSpace;
                  this.selectedDomains = webspace.domains || [];
                  this.selectedCompany = webspace.companyId;
                  this.selectedType = webspace.type;
                  this.selectedTemplate = webspace.template;
                  this.properties = webspace.properties || this.propertiesAll;
                  this.domainDescription = webspace.description;
                  this.createdOn = this.formatDate(webspace.createdOn);
                  if (webspace.modifiedOn) {
                    this.modifiedOn = this.formatDate(webspace.modifiedOn);
                  }
                  return;
                }
                this.err = res.body.statusText;
              })
              .catch((err) => {
                this.err = err.body.statusText || err.body.message;
              });
          } else {
            this.properties = this.propertiesAll;
          }
        });
    },
  };
</script>


<style lang="scss" scoped>
  tr {
    &:nth-of-type(odd){
      background-color: #f9f9f9;
    }
  }
  td {
    border: 1px solid rgba(0, 0, 0, 0.2);
  }
  #json-field {
    position: fixed;
    left: -20000000000px;
  }
  .json-field {
    background: rgba(0, 0, 0, 0.05);
    border: 1px solid rgba(0, 0, 0, 0.1);
  }
</style>




// WEBPACK FOOTER //
// NewWebSpace.vue?0a3fd299